import { Fragment, useMemo, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight, IconMapPin, IconUser } from '@tabler/icons-react';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { GetSiteSetupsBySiteIdQuery, useGetSiteSetupsBySiteIdQuery } from 'data/__generated__';
import { SectionContainer } from 'components/DetailsView/Section';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { Loader } from 'components/Loader';
import { colors } from 'constants/colors';
import { formatDateAsAnniversary } from 'helpers/dateTimes';
import { SiteSetupTrelloItem } from '../components/SiteSetupTrelloItem';
import { SETUP_STATE_COLORS, SITE_SETUP_STATES_LABELS } from '../constants/states';

const SECTION_TITLE = 'Chronologie des rangements du site';

// ISO 8601 week number (weeks start on Monday, week 1 contains the first Thursday of the year).
// Computed by hand because no dayjs week plugin is loaded in totadmin.
function getIsoWeekNumber(date: Date): number {
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNumber = (target.getUTCDay() + 6) % 7; // Monday = 0
    target.setUTCDate(target.getUTCDate() - dayNumber + 3); // Thursday of the current week
    const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3);
    return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

export const OtherSiteSetupSection = ({ siteSetup }: { siteSetup: { siteId: string; date: string; _id: string } }) => {
    const {
        data: otherSiteSetupsData,
        loading: otherSiteSetupsLoading,
        error: otherSiteSetupsError,
    } = useGetSiteSetupsBySiteIdQuery({
        variables: {
            siteId: siteSetup.siteId,
            date: siteSetup.date,
        },
    });

    if (otherSiteSetupsLoading) {
        return (
            <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
                <Loader />
            </SectionContainer>
        );
    }

    if (otherSiteSetupsError || !otherSiteSetupsData) {
        return (
            <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
                <ErrorText>Erreur lors du chargement des autres rangements</ErrorText>
            </SectionContainer>
        );
    }

    const setups = otherSiteSetupsData.getSiteSetupsBySiteId;
    const hasOtherSetups = setups.some((setup) => setup._id !== siteSetup._id);

    if (!hasOtherSetups) {
        return (
            <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
                <NoDataText>Aucun autre rangement trouvé pour ce site</NoDataText>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
            <SetupChronology setups={setups} currentSetupId={siteSetup._id} />
        </SectionContainer>
    );
};

type SetupItem = GetSiteSetupsBySiteIdQuery['getSiteSetupsBySiteId'][number];

const SetupChronology = ({ setups, currentSetupId }: { setups: SetupItem[]; currentSetupId: string }) => {
    // Oldest → newest so the rail reads left (passé) → right (présent/futur).
    const sortedSetups = useMemo(
        () => [...setups].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()),
        [setups],
    );

    const currentIndex = sortedSetups.findIndex((setup) => setup._id === currentSetupId);

    // Group setups by ISO week (Monday → Sunday) while keeping each item's global index so the
    // timeline rail can render week-separated columns without breaking selection/navigation.
    const weekGroups = useMemo(() => {
        const groups: {
            weekKey: string;
            weekNumber: number;
            rangeLabel: string;
            items: { setup: SetupItem; index: number }[];
        }[] = [];

        sortedSetups.forEach((setup, index) => {
            const date = dayjs(setup.date);
            const dayOfWeek = date.day(); // 0 = Sunday
            const monday = date.add(dayOfWeek === 0 ? -6 : 1 - dayOfWeek, 'day');
            const weekKey = monday.format('YYYY-MM-DD');

            const lastGroup = groups[groups.length - 1];
            if (lastGroup?.weekKey === weekKey) {
                lastGroup.items.push({ setup, index });
                return;
            }

            groups.push({
                weekKey,
                weekNumber: getIsoWeekNumber(monday.toDate()),
                rangeLabel: `${monday.format('DD/MM')} – ${monday.add(6, 'day').format('DD/MM')}`,
                items: [{ setup, index }],
            });
        });

        return groups;
    }, [sortedSetups]);

    const [selectedIndex, setSelectedIndex] = useState(Math.max(currentIndex, 0));

    const railRef = useRef<HTMLDivElement>(null);

    const scrollNodeIntoView = (index: number) => {
        const node = railRef.current?.querySelector<HTMLElement>(`[data-node-index="${index}"]`);
        node?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    const selectIndex = (index: number) => {
        const clamped = Math.min(Math.max(index, 0), sortedSetups.length - 1);
        setSelectedIndex(clamped);
        scrollNodeIntoView(clamped);
    };

    const selectedSetup = sortedSetups[selectedIndex];
    const isCurrentSelected = selectedSetup._id === currentSetupId;
    const isFirst = selectedIndex === 0;
    const isLast = selectedIndex === sortedSetups.length - 1;

    return (
        <Wrapper>
            <Rail ref={railRef}>
                <Track>
                    {weekGroups.map((week, weekPosition) => (
                        <Fragment key={week.weekKey}>
                            {weekPosition > 0 ? <WeekSeparator /> : null}
                            <WeekColumn>
                                <WeekLabel>
                                    <WeekNumber>S{week.weekNumber}</WeekNumber>
                                    <WeekRange>{week.rangeLabel}</WeekRange>
                                </WeekLabel>
                                <WeekNodes>
                                    {week.items.map(({ setup, index }) => {
                                        const isSelected = index === selectedIndex;
                                        const isCurrent = setup._id === currentSetupId;
                                        const nodeColor = setup.isFinishForced
                                            ? colors.red
                                            : SETUP_STATE_COLORS[setup.state];

                                        return (
                                            <Node
                                                key={setup._id}
                                                type="button"
                                                data-node-index={index}
                                                $isSelected={isSelected}
                                                onClick={() => selectIndex(index)}
                                                title={`${formatDateAsAnniversary({
                                                    dateTime: setup.date,
                                                    displayDay: true,
                                                })} — ${SITE_SETUP_STATES_LABELS[setup.state]}${
                                                    setup.delivery?.isDynamic ? ' · dynamique' : ''
                                                }`}
                                            >
                                                <NodeDateRow>
                                                    <NodeDate $isSelected={isSelected}>
                                                        {dayjs(setup.date).format('DD/MM')}
                                                    </NodeDate>
                                                    <DynamicDeliveryTag
                                                        isDynamic={setup.delivery?.isDynamic}
                                                        label="Rangement dynamique"
                                                        size={12}
                                                    />
                                                </NodeDateRow>
                                                <Dot $color={nodeColor} $isSelected={isSelected} $isCurrent={isCurrent}>
                                                    {isCurrent ? (
                                                        <IconMapPin size={12} color={colors.pureWhite} />
                                                    ) : null}
                                                </Dot>
                                                <NodeTag $isCurrent={isCurrent}>{isCurrent ? 'actuel' : ''}</NodeTag>
                                            </Node>
                                        );
                                    })}
                                </WeekNodes>
                            </WeekColumn>
                        </Fragment>
                    ))}
                </Track>
            </Rail>

            <NavRow>
                <NavButton type="button" onClick={() => selectIndex(selectedIndex - 1)} disabled={isFirst}>
                    <IconChevronLeft size={18} />
                    <NavButtonLabel>Précédent</NavButtonLabel>
                </NavButton>

                <NavInfo>
                    <NavDate>
                        {formatDateAsAnniversary({ dateTime: selectedSetup.date, displayDay: true })}
                        {isCurrentSelected ? <CurrentBadge>actuel</CurrentBadge> : null}
                    </NavDate>
                    <NavMeta>
                        <StateChip
                            $color={selectedSetup.isFinishForced ? colors.red : SETUP_STATE_COLORS[selectedSetup.state]}
                        >
                            {selectedSetup.isFinishForced
                                ? 'Finition forcée'
                                : SITE_SETUP_STATES_LABELS[selectedSetup.state]}
                        </StateChip>
                        <PositionText>
                            n°{selectedIndex + 1} sur {sortedSetups.length}
                        </PositionText>
                        <DynamicDeliveryTag isDynamic={selectedSetup.delivery?.isDynamic} label="Rangement dynamique" />
                        {selectedSetup.deliveryPerson ? (
                            <DeliveryPerson>
                                <IconUser size={14} />
                                {selectedSetup.deliveryPerson.fullname}
                            </DeliveryPerson>
                        ) : null}
                    </NavMeta>
                </NavInfo>

                <NavButton type="button" onClick={() => selectIndex(selectedIndex + 1)} disabled={isLast}>
                    <NavButtonLabel>Suivant</NavButtonLabel>
                    <IconChevronRight size={18} />
                </NavButton>
            </NavRow>

            {isCurrentSelected ? null : (
                <CardArea>
                    <SiteSetupTrelloItem siteSetup={selectedSetup} showDate />
                </CardArea>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Rail = styled.div`
    overflow-x: auto;
    padding: 4px 0 2px;

    scrollbar-width: thin;
    &::-webkit-scrollbar {
        height: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.lightBorderColor};
        border-radius: 3px;
    }
`;

const Track = styled.div`
    display: flex;
    align-items: stretch;
    justify-content: center;
    min-width: 100%;
    padding: 0 8px;
`;

const WeekSeparator = styled.div`
    flex-shrink: 0;
    align-self: stretch;
    width: 0;
    margin: 2px 6px;
    border-left: 1px dashed ${({ theme }) => theme.lightBorderColor};
`;

const WeekColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const WeekLabel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    white-space: nowrap;
`;

const WeekNumber = styled.span`
    font-size: 11px;
    font-weight: 800;
    color: ${({ theme }) => theme.textColor};
`;

const WeekRange = styled.span`
    font-size: 10px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const WeekNodes = styled.div`
    position: relative;
    display: flex;
    align-items: flex-start;

    /* The chronological line behind the dots of this week. */
    &::before {
        content: '';
        position: absolute;
        top: 28px;
        left: 12px;
        right: 12px;
        height: 2px;
        background: ${({ theme }) => theme.lightBorderColor};
        z-index: 0;
    }
`;

const Node = styled.button<{ $isSelected: boolean }>`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 64px;
    padding: 0 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};

    &:hover > div {
        transform: scale(1.15);
    }
`;

const NodeDateRow = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const NodeDate = styled.span<{ $isSelected: boolean }>`
    font-size: 11px;
    font-weight: ${({ $isSelected }) => ($isSelected ? 800 : 500)};
    color: ${({ $isSelected, theme }) => ($isSelected ? theme.textColor : theme.infoTextColor)};
    white-space: nowrap;
`;

const Dot = styled.div<{ $color: string; $isSelected: boolean; $isCurrent: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ $isSelected }) => ($isSelected ? '20px' : '14px')};
    height: ${({ $isSelected }) => ($isSelected ? '20px' : '14px')};
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    box-shadow: ${({ $isSelected, $color, theme }) =>
        $isSelected ? `0 0 0 4px ${theme.cardBackgroundColor}, 0 0 0 6px ${$color}` : 'none'};
    outline: ${({ $isCurrent }) => ($isCurrent ? `2px dashed ${colors.blue}` : 'none')};
    outline-offset: 2px;
    transition: all 0.15s ease;
`;

const NodeTag = styled.span<{ $isCurrent: boolean }>`
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-height: 12px;
    color: ${({ $isCurrent }) => ($isCurrent ? colors.blue : 'transparent')};
`;

const NavRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const NavButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 14px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.backgroundColor};
        border-color: ${({ theme }) => theme.darkBorderColor};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const NavButtonLabel = styled.span`
    @media (max-width: 600px) {
        display: none;
    }
`;

const NavInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
    flex: 1;
    min-width: 0;
`;

const NavDate = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 800;
    text-transform: capitalize;
    color: ${({ theme }) => theme.textColor};
`;

const CurrentBadge = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.pureWhite};
    background-color: ${colors.blue};
    padding: 1px 6px;
    border-radius: 999px;
`;

const NavMeta = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const StateChip = styled.span<{ $color: string }>`
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: 700;
    color: ${colors.pureWhite};
    background-color: ${({ $color }) => $color};
`;

const PositionText = styled.span`
    font-weight: 600;
`;

const DeliveryPerson = styled.span`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const CardArea = styled.div``;

const ErrorText = styled.div`
    color: ${({ theme }) => theme.errorColor};
    text-align: center;
    padding: 20px;
`;

const NoDataText = styled.div`
    color: ${({ theme }) => theme.textColor};
    text-align: center;
    padding: 20px;
    font-style: italic;
`;
